import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, Volume2, Vibrate, MonitorSmartphone, BellRing, Play, Upload, Music, Ticket, CheckCircle2, MessageSquare, Mail } from "lucide-react";
import {
  getNotifPrefs, setNotifPrefs, requestNotifPermission, notifPermission, notifSupported, fireNotification,
  NOTIF_CATEGORIES, SOUND_PRESETS, previewSound, getCustomSounds, setCustomSound,
} from "../lib/notifications";
import { InstallAppButton } from "./InstallAppButton";

const CAT_ICON = { ticket: Ticket, validation: CheckCircle2, chat: MessageSquare, email: Mail };

function Toggle({ testid, checked, onChange, icon: Icon, label, desc }) {
  return (
    <label className="flex items-center gap-3 py-3 cursor-pointer">
      <div className="w-9 h-9 rounded-xl bg-klein/15 text-klein flex items-center justify-center shrink-0"><Icon size={17} /></div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[12px] text-zinc-500">{desc}</div>
      </div>
      <button type="button" data-testid={testid} onClick={() => onChange(!checked)} role="switch" aria-checked={checked}
        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? "bg-klein" : "bg-zinc-400/40"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );
}

function SoundPicker({ testid, slot, value, onChange, icon: Icon, label, desc }) {
  const fileRef = useRef();
  const [hasCustom, setHasCustom] = useState(!!getCustomSounds()[slot]);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("Fichier trop lourd (max 1 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setCustomSound(slot, reader.result);
      setHasCustom(true);
      onChange("custom");
      toast.success("Son personnalisé enregistré");
      try { const a = new Audio(reader.result); a.play().catch(() => {}); } catch {}
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex items-center gap-3 py-2.5" data-testid={testid}>
      {Icon && <div className="w-8 h-8 rounded-lg bg-white/[0.06] text-zinc-300 flex items-center justify-center shrink-0"><Icon size={15} /></div>}
      <div className="min-w-0 flex-1">
        {label && <div className="text-sm font-medium">{label}</div>}
        {desc && <div className="text-[11px] text-zinc-500">{desc}</div>}
      </div>
      <select data-testid={`${testid}-select`} value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-background border border-white/10 rounded-lg px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 max-w-[150px]">
        {SOUND_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
      {value === "custom" && (
        <button type="button" data-testid={`${testid}-upload`} onClick={() => fileRef.current.click()} title="Importer un son"
          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${hasCustom ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"} hover:brightness-110`}>
          <Upload size={15} />
        </button>
      )}
      <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={upload} data-testid={`${testid}-input`} />
      <button type="button" data-testid={`${testid}-preview`} onClick={() => previewSound(value, slot)} title="Écouter"
        className="w-9 h-9 rounded-lg bg-klein/15 text-klein flex items-center justify-center shrink-0 hover:bg-klein/25"><Play size={15} /></button>
    </div>
  );
}

export function NotificationSettings() {
  const [prefs, setPrefs] = useState(getNotifPrefs());
  const [perm, setPerm] = useState(notifPermission());

  useEffect(() => { setNotifPrefs(prefs); }, [prefs]);
  const upd = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));
  const updSound = (cat, v) => setPrefs((p) => ({ ...p, sounds: { ...p.sounds, [cat]: v } }));

  const enableDesktop = async (v) => {
    if (v && notifSupported() && Notification.permission !== "granted") {
      const res = await requestNotifPermission();
      setPerm(res);
      if (res !== "granted") { toast.error("Autorisation refusée par le navigateur"); upd("desktop", false); return; }
    }
    upd("desktop", v);
  };

  const test = () => { fireNotification("FlowDesk — Test 🔔", "Voici à quoi ressemblent vos notifications.", "email"); toast("Notification de test envoyée"); };

  return (
    <div className="mt-8" data-testid="notification-settings-section">
      <div className="flex items-center gap-2 mb-1">
        <Bell size={18} className="text-klein" />
        <h2 className="font-cabinet font-bold text-xl tracking-tight">Notifications & application</h2>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Choisissez comment être alerté des nouveaux emails, messages et tickets, et installez l'app sur vos appareils.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-white/10 rounded-2xl p-5 divide-y divide-white/[0.06]">
          <Toggle testid="notif-toggle-sound" checked={prefs.sound} onChange={(v) => upd("sound", v)} icon={Volume2} label="Son" desc="Jouer un son à chaque notification." />
          <Toggle testid="notif-toggle-vibration" checked={prefs.vibration} onChange={(v) => upd("vibration", v)} icon={Vibrate} label="Vibreur" desc="Vibrer sur mobile / tablette (si supporté)." />
          <Toggle testid="notif-toggle-desktop" checked={prefs.desktop && perm === "granted"} onChange={enableDesktop} icon={MonitorSmartphone} label="Notifications système" desc="Afficher dans le centre de notifications avec le logo." />
          <div className="pt-3 flex items-center justify-between">
            <span className="text-[12px] text-zinc-500">
              {perm === "granted" ? "Autorisées par le navigateur" : perm === "denied" ? "Bloquées — réactivez-les dans le navigateur" : "Autorisation non demandée"}
            </span>
            <button data-testid="notif-test" onClick={test} className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-klein/15 text-klein text-xs font-semibold hover:bg-klein/25"><BellRing size={14} /> Tester</button>
          </div>
        </div>

        {/* Sons par type d'événement */}
        <div className={`bg-card border border-white/10 rounded-2xl p-5 ${prefs.sound ? "" : "opacity-50 pointer-events-none"}`} data-testid="notif-sounds-section">
          <div className="flex items-center gap-2 mb-1"><Music size={16} className="text-klein" /><h3 className="font-cabinet font-semibold text-sm">Sons</h3></div>
          <p className="text-[12px] text-zinc-500 mb-3">Un son unique pour tout, ou un son différent par type d'événement.</p>

          <div className="flex gap-1 p-1 bg-white/[0.06] rounded-lg mb-3">
            <button data-testid="notif-mode-single" onClick={() => upd("perEvent", false)} className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${!prefs.perEvent ? "bg-card shadow-sm text-foreground" : "text-zinc-500"}`}>Un son pour tout</button>
            <button data-testid="notif-mode-perevent" onClick={() => upd("perEvent", true)} className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${prefs.perEvent ? "bg-card shadow-sm text-foreground" : "text-zinc-500"}`}>Un son par type</button>
          </div>

          {!prefs.perEvent ? (
            <SoundPicker testid="notif-sound-all" slot="all" value={prefs.sound_all} onChange={(v) => upd("sound_all", v)} icon={Volume2} label="Son de notification" desc="Utilisé pour toutes les alertes." />
          ) : (
            <div className="divide-y divide-white/[0.06]">
              {NOTIF_CATEGORIES.map((c) => (
                <SoundPicker key={c.key} testid={`notif-sound-${c.key}`} slot={c.key} value={prefs.sounds[c.key]} onChange={(v) => updSound(c.key, v)} icon={CAT_ICON[c.key]} label={c.label} desc={c.desc} />
              ))}
            </div>
          )}
        </div>

        <InstallAppButton />
      </div>
    </div>
  );
}
